import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { curseName, cursesNow } from "../../../engine/bench";
import { funBoard, mainBoard } from "../../../engine/ranking";
import { courtOf, currentRound, displayName, hasJoined, lostLast, playerGames } from "../../../engine/selectors";
import { gamesDone } from "../../../engine/winddown";
import type { CourtGame, GameRecord, Player, PlayerId, PlayerStatus, Round, TournamentState } from "../../../engine/types";
import { Face } from "../components/Face";
import { FitText } from "../components/FitText";
import { MascotSays } from "../components/Mascot";
import { useLook } from "../lib/look";
import { isFavourite, lineFor, poseFor, type MascotMoment } from "../lib/mascot";
import { useTournament, type Screen, type Tournament } from "../lib/useTournament";
import { STATUS_WORDS } from "../lib/transport";
import { BREAK_OVER, breakNow, countdown } from "../../../shared/day";
import { demoDay, demoDayFromLocation } from "./projector/demoDay";
import { mmss, msLeft, useTick } from "./court/clock";
import { Bench } from "./player/Bench";
import { Boards } from "./player/Boards";
import { useDoor, useTripleTap } from "./player/Door";
import { NicknameStep, PictureStep, PinStep, WhoStep } from "./player/Join";
import { useKeyboardUp } from "./player/keyboard";
import { MyGames } from "./player/MyGames";
import { NamingRight } from "./player/NamingRight";
import { Profile } from "./player/Profile";
import { useRemembered } from "./player/remember";
import "./Player.css";
import "./player/Phone.css";

/**
 * The name this phone picked last, on this site (not per tournament): the PIN step opens on it.
 * Who the phone IS comes from its session for the tournament (decision 39), which the server
 * checks on every connection.
 */
const ME_KEY = "bd.me";

function readMe(): PlayerId | null {
  try {
    return localStorage.getItem(ME_KEY);
  } catch {
    return null;
  }
}

function saveMe(id: PlayerId | null): void {
  try {
    if (id) localStorage.setItem(ME_KEY, id);
    else localStorage.removeItem(ME_KEY);
  } catch {
    // without storage the choice lasts until the page closes
  }
}

export function PlayerView({ screen }: { screen: Screen }) {
  // the name picked on this phone; who it is for the server is the identity it got in as
  const [picked, setPicked] = useState(readMe);
  const t = useTournament(screen, picked ? `player:${picked}` : undefined);
  const me = t.identity?.role === "player" ? t.identity.player ?? null : null;
  const door = useDoor(t);
  useTripleTap(door.open);
  const [profile, setProfile] = useState(false);
  // The join flow, once begun on this page, runs on to the nickname step even though the
  // picture alone already counts as joined.
  const joining = useRef(false);
  // What Now last showed, kept here so it outlives a look at the profile.
  const news = useRef<NowNews>({ seen: null, fresh: false });
  const [nicknamed, setNicknamed] = useState(false);
  useTick(1000);
  useLook(t.state?.config.look);
  // ?demo=lunch and the like: see projector/demoDay.ts
  const pretend = useRef(demoDayFromLocation()).current;
  const openedAt = useRef(t.now()).current;
  const s = t.state && pretend ? demoDay(t.state, pretend, openedAt) : t.state;

  // the phone got in as a player: that is the name it opens on from now on
  useEffect(() => {
    if (me && me !== picked) {
      saveMe(me);
      setPicked(me);
    }
  }, [me]);

  /** A name picked (or none, to pick again): the PIN step comes next. */
  function pick(id: PlayerId | null) {
    saveMe(id);
    setPicked(id);
    joining.current = false;
    setProfile(false);
    setNicknamed(false);
  }

  /** "Not you?": this phone forgets who it is, here and on the server, and picks again. */
  function notMe() {
    t.forget();
    pick(null);
  }

  const frame = (body: ReactNode) => (
    <div className="close player-screen">
      <span className={"status-pill " + t.status}>{STATUS_WORDS[t.status]}</span>
      {body}
      {door.sheet}
    </div>
  );

  if (!s || !t.identity) return frame(<p className="player-quiet">Connecting to the tournament</p>);

  // its PIN was reset (or the day started again): say so, then pick a name again
  if (t.signedOut?.as === "player") {
    return frame(
      <>
        <h1>Pick your name again</h1>
        <p className="player-refused" role="alert">{t.signedOut.reason}</p>
        <button type="button" className="btn accent" onClick={() => { t.clearSignedOut(); pick(null); }}>
          Pick my name
        </button>
      </>,
    );
  }

  if (!me) {
    // picked a name (now, or on an earlier visit before PINs): its PIN; otherwise the names
    const named = picked ? s.players[picked] : undefined;
    if (!named) return frame(<WhoStep t={t} state={s} onChoose={pick} />);
    return frame(<PinStep key={named.id} t={t} player={named} onBack={() => pick(null)} />);
  }

  const player = s.players[me];
  if (!player) return frame(<WhoStep t={t} state={s} onChoose={(id) => { t.forget(); pick(id); }} />);

  if (!hasJoined(player)) joining.current = true;
  if (joining.current && !nicknamed) {
    return frame(
      hasJoined(player) ? (
        <NicknameStep t={t} state={s} player={player} onDone={() => setNicknamed(true)} />
      ) : (
        <PictureStep t={t} player={player} onStored={() => undefined} onNotMe={notMe} />
      ),
    );
  }

  if (profile) {
    return frame(<Profile t={t} state={s} player={player} onClose={() => setProfile(false)} onNotMe={notMe} />);
  }

  return (
    <>
      <Phone t={t} state={s} me={me} player={player} news={news.current} onProfile={() => setProfile(true)} />
      {door.sheet && <div className="close player-screen">{door.sheet}</div>}
    </>
  );
}

type TabId = "now" | "games" | "board";
const TABS: readonly TabId[] = ["now", "games", "board"];
const TAB_NAME: Record<TabId, string> = { now: "Now", games: "Games", board: "Board" };

/** What on Now is worth a look: my next game, the round I am playing in, a pause. */
interface NowSeen {
  /** "round:court" of my next game once it is drawn. */
  drawn: string | null;
  /** The round being played with me in it. */
  on: number | null;
  paused: boolean;
}

interface NowNews {
  /** Null until the phone has shown once on this page. */
  seen: NowSeen | null;
  /** Something changed on Now while another tab was open. */
  fresh: boolean;
}

/**
 * The phone once its owner has joined: who I am along the top, three tabs along the bottom.
 * Now is my game, my numbers, the bench and whether I am here, on one screen. Games is what I
 * have played. Board is the two boards with my row pinned. Changing tab adds nothing to the
 * browser's history; the tab is remembered for as long as the browser tab lives. Now opens by
 * itself only when the page loads with a game of mine on or drawn, and when a round I am in starts.
 */
function Phone({
  t,
  state: s,
  me,
  player,
  news,
  onProfile,
}: {
  t: Tournament;
  state: TournamentState;
  me: PlayerId;
  player: Player;
  news: NowNews;
  onProfile(): void;
}) {
  const [tab, setTab] = useRemembered<TabId>("bd.player.tab", TABS, "now");
  const [leaving, setLeaving] = useState(false);
  const [sentId, setSentId] = useState<string | null>(null);
  // A bar pinned to the bottom has no business beside a keyboard: it goes while one is up.
  const typing = useKeyboardUp();

  const main = mainBoard(s);
  const fun = funBoard(s);
  const row = main.find((r) => r.id === me);
  const funRow = fun.find((r) => r.id === me);

  // What is new on a tab since it was last looked at: a result of mine, my place on the board.
  const results = playerGames(s, me).filter((g) => g.won !== null).length;
  const rank = row?.rank ?? null;
  const [seenResults, setSeenResults] = useState(results);
  const [seenRank, setSeenRank] = useState(rank);
  if (tab === "games" && seenResults !== results) setSeenResults(results);
  if (tab === "board" && seenRank !== rank) setSeenRank(rank);

  // Now: my next game is drawn, a round I am in starts, lunch or a pause begins or ends.
  const round = currentRound(s);
  const playing = round && s.clock.phase === "playing" && s.clock.roundNo === round.no ? courtOf(round.lineup, me) : null;
  const drawnOn = s.draft ? courtOf(s.draft, me) : null;
  const at: NowSeen = {
    drawn: drawnOn ? `${s.rounds.length + 1}:${drawnOn.court}` : null,
    on: playing && round ? round.no : null,
    paused: s.pausedAll,
  };
  const [seen, setSeen] = useState(news.seen);
  const [nowFresh, setNowFresh] = useState(news.fresh);
  if (!seen) {
    // The page has just loaded: straight to my game if I have one on or drawn.
    setSeen(at);
    if ((at.on !== null || at.drawn !== null) && tab !== "now") setTab("now");
  } else if (seen.drawn !== at.drawn || seen.on !== at.on || seen.paused !== at.paused) {
    setSeen(at);
    if (tab !== "now") {
      if (at.on !== null && at.on !== seen.on) setTab("now");
      else if ((at.drawn !== null && at.drawn !== seen.drawn) || at.paused !== seen.paused) setNowFresh(true);
    }
  }
  if (tab === "now" && nowFresh) setNowFresh(false);
  news.seen = seen ?? at;
  news.fresh = nowFresh;
  const fresh: Record<TabId, boolean> = { now: nowFresh, games: results > seenResults, board: rank !== seenRank };

  // A tab always opens from its top, whoever opened it.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  const setStatus = (status: PlayerStatus): void => {
    setLeaving(false);
    setSentId(t.send({ type: "player.status", id: me, status }, { queue: true }));
  };
  const show = (id: TabId): void => {
    setLeaving(false);
    setTab(id);
    window.scrollTo(0, 0);
  };

  const waiting = t.queue.length;
  const refused = t.refusal && t.refusal.id === sentId ? t.refusal.reason : null;
  const myName = displayName(player, "phone");
  const here = player.status === "in" || player.status === "break";
  const renamer = player.dub ? s.players[player.dub.by] : undefined;
  const onBreak = breakNow(s, t.now());
  // What the host talks about: what this player is doing right now.
  const moment: MascotMoment = s.pausedAll
    ? "pause"
    : dayDone(s)
      ? "results"
      : !round || player.status === "expected"
      ? "idle"
      : courtOf(round.lineup, me)
        ? "playing"
        : "resting";
  const short = row ? s.config.minGamesForPodium - row.games : 0;
  // I'm here and I'm back live in the hero. Only someone on a break or leaving in the middle of
  // their own game has no room for it there, so it stays at the bottom for them.
  const myCourt = myCourtNow(s, me);
  const backBelow = (player.status === "break" || player.status === "left") && !!myCourt && !myCourt.over;

  return (
    <div className="close player-screen pn">
      <header className="player-who">
        <button type="button" className="player-who-face" aria-label="Your picture and nickname" onClick={onProfile}>
          <Face name={myName} src={t.faceOf(player)} stamped={lostLast(s, me)} crowned={isFavourite(me)} />
        </button>
        <div className="player-who-names">
          <h1>{keepEmoji(myName)}</h1>
          {myName !== player.name && <p>{player.name}</p>}
        </div>
        <span className={"status-pill pn-pill " + t.status}>{STATUS_WORDS[t.status]}</span>
        <button type="button" className="player-link" onClick={onProfile}>
          profile
        </button>
      </header>

      {tab === "now" && (
        <div key="now" className="pn-tab pn-now">
          {player.dub && (
            <p className="pn-renamed">
              Renamed until round {player.dub.untilRound} by {renamer ? displayName(renamer, "phone") : "a winner"}
            </p>
          )}

          {onBreak && (
            <section className="player-break" role="status">
              <h2>{onBreak.label}</h2>
              <p className="player-break-back">{onBreak.over ? BREAK_OVER : onBreak.backAt}</p>
              {!onBreak.over && onBreak.leftMs !== null && <p className="player-break-count">{`${countdown(onBreak.leftMs)} to go`}</p>}
            </section>
          )}

          <NextGame state={s} me={me} t={t} onHere={() => setStatus("in")} />

          <NamingRight t={t} state={s} me={me} />

          {/* The host has its say between games. On court the room goes to the game. */}
          {moment !== "playing" && !onBreak && (
            <MascotSays side="left" pose={poseFor(moment)} line={hostLine(s, player, moment, round?.no ?? 0)} className="player-host" />
          )}

          {row && (
            <section className="pn-numbers" aria-label="My numbers">
              <div className="pn-tiles">
                <div>
                  <strong>{row.games}</strong>
                  <span>games</span>
                </div>
                <div>
                  <strong>{row.wins}</strong>
                  <span>wins</span>
                </div>
                <div>
                  <strong>{row.avg.toFixed(1)}</strong>
                  <span>avg</span>
                </div>
                <div>
                  {/* everyone is level before a game: no rank to show until the first one */}
                  <strong>{row.games ? row.rank : "-"}</strong>
                  <span>rank</span>
                </div>
                {funRow && (
                  <div className="pn-fun">
                    <strong>{Math.round(funRow.funPoints)}</strong>
                    <span>fun</span>
                  </div>
                )}
              </div>
              {!row.eligible && !onBreak && (
                <p className="player-podium">
                  {short === 1 ? "1 more game to count for the podium" : `${short} more games to count for the podium`}
                </p>
              )}
            </section>
          )}

          {here && <Bench t={t} state={s} me={me} bare={!!onBreak} />}

          {(player.status !== "left" || backBelow) && (
            <section className="pn-status" aria-label="Here or not">
              {backBelow && (
                <button type="button" className="btn" onClick={() => setStatus("in")}>
                  {player.status === "left" ? "I'm here" : "I'm back"}
                </button>
              )}
              {player.status === "in" && (
                <button type="button" className="btn quiet" onClick={() => setStatus("break")}>
                  On a break
                </button>
              )}
              {player.status !== "left" && (
                <button type="button" className="btn warn" onClick={() => setLeaving(true)}>
                  Leaving
                </button>
              )}
            </section>
          )}
          {waiting > 0 && (
            <p className="player-quiet">{t.status === "open" ? "Sending" : "Waiting to send. It goes when you are back online."}</p>
          )}
          {refused && <p className="player-refused">Not saved: {refused}</p>}
        </div>
      )}

      {tab === "games" && (
        <div key="games" className="pn-tab">
          <MyGames state={s} me={me} />
        </div>
      )}

      {tab === "board" && (
        <div key="board" className="pn-tab">
          <Boards main={main} fun={fun} me={me} players={s.players} />
        </div>
      )}

      {!typing && (
        <nav className="pn-tabs" aria-label="Sections">
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              className={"pn-tabbtn" + (tab === id ? " on" : "")}
              aria-current={tab === id ? "page" : undefined}
              onClick={() => show(id)}
            >
              {TAB_NAME[id]}
              {fresh[id] && (
                <span className="pn-badge">
                  <span className="pn-sr">, new</span>
                </span>
              )}
            </button>
          ))}
        </nav>
      )}

      {leaving && (
        <div className="pn-sheet" role="alertdialog" aria-label="Leaving">
          <p>Sure? You will not be drawn again unless you tap I'm here.</p>
          <div className="player-confirm-actions">
            <button type="button" className="btn quiet" onClick={() => setLeaving(false)}>
              Stay
            </button>
            <button type="button" className="btn warn" onClick={() => setStatus("left")}>
              Yes, I'm leaving
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * No more rounds to draw: the wind down's last round is drawn (it may still be on), or the boards
 * are being shown. Nothing about a next round is said or guessed then.
 */
function noMoreRounds(state: TournamentState): boolean {
  return gamesDone(state) || state.reveal.main || state.reveal.fun || state.reveal.awards;
}

/** No more games today: the wind down's last round is over, or the boards are being shown. */
function dayDone(state: TournamentState): boolean {
  return (gamesDone(state) && state.clock.phase !== "playing") || state.reveal.main || state.reveal.fun || state.reveal.awards;
}

/** My court in the round being played, its record, and whether its score is in (or it was voided). */
function myCourtNow(
  state: TournamentState,
  me: PlayerId,
): { round: Round; game: CourtGame; record: GameRecord | undefined; over: boolean } | null {
  const round = currentRound(state);
  if (!round || state.clock.phase !== "playing" || state.clock.roundNo !== round.no) return null;
  const game = courtOf(round.lineup, me);
  if (!game) return null;
  const record = round.games.find((g) => g.court === game.court);
  return { round, game, record, over: !!record && (record.voided || record.score !== null) };
}

/**
 * Before the next round is drawn, a guess from the rest rule at whether I am in it: those resting
 * now go on first. Only when the numbers say so: everyone on court sits out next only when those
 * here and resting would fill every place, and someone resting is on next only when they all fit.
 * No guess in Mexicano, where the draw waits for the scores, nor once the games are done.
 */
function restGuess(state: TournamentState, me: PlayerId): "out" | "on" | null {
  const round = currentRound(state);
  if (state.draft || !round || state.players[me]?.status !== "in" || state.mode === "mexicano" || noMoreRounds(state)) return null;
  const places = state.config.courts * 4;
  const resting = round.lineup.sittingOut.filter((id) => state.players[id]?.status === "in").length;
  if (courtOf(round.lineup, me)) return resting >= places ? "out" : null;
  if (round.lineup.sittingOut.includes(me)) return resting <= places ? "on" : null;
  return null;
}

/**
 * The host's line for this moment, the same on every phone in the same round, but never one that
 * tells someone to do what they cannot: curse with their curse thrown or curses off, heckle while
 * heckles are off. Then it is the next line along.
 */
function hostLine(state: TournamentState, player: Player, moment: MascotMoment, seed: number): string {
  const cannot = (line: string): boolean =>
    (/curse/i.test(line) && (player.cursesLeft < 1 || !!state.bench.cursesOff)) || (/heckle/i.test(line) && state.bench.muted);
  for (let i = 0; i < 4; i += 1) {
    const line = lineFor(moment, seed + i);
    if (!cannot(line)) return line;
  }
  return lineFor(moment, seed);
}

/** A name that ends in an emoji keeps it on the name's line: the crown of "Chandelier 👑" is never alone. */
function keepEmoji(name: string): string {
  return name.replace(/ (\p{Extended_Pictographic}[\p{Extended_Pictographic}\p{Emoji_Modifier}‍️]*)$/u, " $1");
}

function GameLine({ state, game, me, t }: { state: TournamentState; game: CourtGame; me: PlayerId; t: Tournament }) {
  const mine = game.a.includes(me) ? game.a : game.b;
  const theirs = mine === game.a ? game.b : game.a;
  const name = (id: PlayerId): string => displayName(state.players[id], "phone");
  // the wall's name first, and under a nickname the real one small, to find them in the hall
  const who = (id: PlayerId, and = false) => {
    const real = state.players[id]?.name;
    return (
      <span key={id} className="player-other">
        <Face name={name(id)} src={t.faceOf(state.players[id])} />
        <span className="player-other-names">
          {/* 0.8 of 20 px: a name never goes under 16 px */}
          <FitText min={0.8}>{name(id) + (and ? "\u00a0&" : "")}</FitText>
          {real && real !== name(id) ? <small className="player-real">{real}</small> : null}
        </span>
      </span>
    );
  };
  const partner = mine.find((id) => id !== me);
  return (
    <div className="player-game">
      <div className={`player-court court-${game.court}`}>
        <span>Court</span>
        <strong>{game.court}</strong>
      </div>
      <div className="player-teams">
        <p>
          <span className="player-label">With</span>
          {partner ? who(partner) : ""}
        </p>
        <p>
          <span className="player-label">Against</span>
          <span className="player-others">{theirs.map((id, i) => who(id, i < theirs.length - 1))}</span>
        </p>
      </div>
    </div>
  );
}

function NextGame({ state, me, t, onHere }: { state: TournamentState; me: PlayerId; t: Tournament; onHere(): void }) {
  const now = t.now();
  const status = state.players[me]?.status;
  const round = currentRound(state);
  const mine = myCourtNow(state, me);
  // once the last round is drawn no next round is, whatever is lying about
  const done = dayDone(state);
  const last = noMoreRounds(state);
  const next = state.draft && !last ? courtOf(state.draft, me) : null;
  const sittingNext = !last && !!state.draft?.sittingOut.includes(me);
  const left = msLeft(state, now);
  const nextNo = state.rounds.length + 1;
  // Only a guess from the rest rule until the draw is made, so always "probably" or "likely".
  const guess = restGuess(state, me);
  const estimate = sittingNext
    ? `You sit out round ${nextNo}. Likely back on court in round ${nextNo + 1}.`
    : guess === "out"
      ? "You will probably sit out the next round."
      : guess === "on"
        ? "You are probably up next round."
        : null;

  // Under a game of mine there is room for one short line about the round after it.
  const then = next ? `court ${next.court}` : sittingNext ? "sitting out" : guess === "out" ? "probably sitting out" : null;
  const nextLine = then ? <p className="player-then">Next round: {then}</p> : null;

  // The hero card: a game fills it with its court's colour, anything else is said on paper.
  const card = (body: ReactNode, court?: number) => (
    <section key={`${round?.no ?? 0}:${court ?? 0}:${status}`} className={"player-hero" + (court ? ` court-${court}` : "")}>
      {body}
    </section>
  );
  const text = (words: string) => card(<p className="player-hero-text">{words}</p>);
  // The words tell them to tap it, so the button is right there under the words.
  const ask = (words: string, button: string) =>
    card(
      <>
        <p className="player-hero-text">{words}</p>
        <button type="button" className="btn accent" onClick={onHere}>
          {button}
        </button>
      </>,
    );
  // What it is and when, on one line: the heading on the left, the clock on the right.
  const band = (title: string, clock: string | null) => (
    <div className="pn-hero-band">
      <h2>{title}</h2>
      {clock && <p className="pn-hero-clock">{clock}</p>}
    </div>
  );

  // On my court this round. A game still going stays on screen after a tap on Leaving or On a
  // break, with a line to finish it. Once its score is in it says done, for someone still here;
  // anyone else goes on to what their status says.
  if (mine && (!mine.over || status === "in")) {
    const { game, record, over } = mine;
    const side = game.a.includes(me) ? "a" : "b";
    // while it is on: a curse on my pair while it runs, and a bounty on my head
    const curse = over ? undefined : cursesNow(state, now).find((c) => c.court === game.court && c.side === side);
    const bounty = over ? undefined : mine.round.bounties.find((b) => b.player === me);
    const flags = (curse || bounty) && (
      <ul className="player-flags">
        {curse && (
          <li>
            {curse.stepsOff ? (curse.stepsOff === me ? "Solo: you step off" : "Solo: you play alone") : curseName(curse)}, {mmss(curse.leftMs)}. Cursed by{" "}
            {displayName(state.players[curse.by], "phone")}
          </li>
        )}
        {bounty && <li>Bounty on you: worth {bounty.value}</li>}
      </ul>
    );
    // my side's points first, as on the Games tab
    const score = record?.score ? (side === "a" ? `${record.score.a}-${record.score.b}` : `${record.score.b}-${record.score.a}`) : null;
    const finish =
      status === "left" ? "Finish this game. After it you are out of the draw." : status === "break" ? "Finish this game, then your break starts." : null;
    return card(
      <>
        {over
          ? band("My game: done", record?.voided ? "void" : score)
          : band("My game: now", state.pausedAll ? "paused" : left !== null ? `${mmss(left)} left` : null)}
        <GameLine state={state} game={game} me={me} t={t} />
        {flags}
        {/* a curse lasts a minute, and for that minute it has the line */}
        {finish ? <p className="player-then pn-finish">{finish}</p> : !curse && nextLine}
      </>,
      game.court,
    );
  }

  if (status === "left") return ask("You have left. Tap I'm here if you are back.", "I'm here");
  if (status === "expected") return ask("Tap I'm here when you arrive.", "I'm here");
  if (done) return text("The games are done. Eyes on the big screen.");
  if (last) return text("This is the last round. You are not in it.");
  if (status === "break") return ask("You are on a break. Tap I'm back to be drawn again.", "I'm back");
  if (next) {
    return card(
      <>
        {band("My next game", "next round")}
        <GameLine state={state} game={next} me={me} t={t} />
      </>,
      next.court,
    );
  }
  if (estimate) return text(estimate);
  if (!state.draft) return text("Your next game is drawn when the scores are in.");
  return text("You are not in the next round. You will be in the draw after it.");
}
